<script setup lang="ts">
// Page-title-adjacent "Create new X" / section-heading "Add X" action button.
//
// On mobile (< sm / 640px) it collapses to an icon-only "+" square with a
// comfortable ~40px tap target; from sm upwards it renders exactly like the
// original inline-flex button (icon + full label). The accessible name is
// always the full translated label via `aria-label`, which takes precedence
// over the (visually hidden on mobile) text node, so there is never a
// duplicated or missing accessible name at either breakpoint.
//
// Renders a RouterLink when `to` is provided, otherwise a <button> that
// emits `click` (used by pages that create the resource via an API call
// before navigating, e.g. NewsletterListPage).
withDefaults(
  defineProps<{
    /** Full translated label - used as the accessible name and the sm+ visible text. */
    label: string
    /** Route to navigate to. Omit to render a <button> instead. */
    to?: string
    /** Disables the button variant while an action is in flight. Ignored for the RouterLink variant. */
    disabled?: boolean
    /** Use the slightly tighter `py-2` padding some pages use on sm+ instead of the default `py-2.5`. */
    compact?: boolean
    /** Use the tighter `gap-1.5` spacing between icon and label some section-heading "Add X" buttons use on sm+ instead of the default `gap-2`. */
    tightGap?: boolean
  }>(),
  {
    to: undefined,
    disabled: false,
    compact: false,
    tightGap: false,
  },
)

const emit = defineEmits<{
  click: []
}>()

function handleClick(): void {
  emit('click')
}
</script>

<template>
  <RouterLink
    v-if="to"
    :to="to"
    :aria-label="label"
    class="inline-flex items-center bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg p-3 text-sm transition-colors"
    :class="[tightGap ? 'gap-1.5' : 'gap-2', compact ? 'sm:px-4 sm:py-2' : 'sm:px-4 sm:py-2.5']"
  >
    <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
    </svg>
    <span class="hidden sm:inline">{{ label }}</span>
  </RouterLink>
  <button
    v-else
    type="button"
    :disabled="disabled"
    :aria-label="label"
    class="inline-flex items-center bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold rounded-lg p-3 text-sm transition-colors"
    :class="[tightGap ? 'gap-1.5' : 'gap-2', compact ? 'sm:px-4 sm:py-2' : 'sm:px-4 sm:py-2.5']"
    @click="handleClick"
  >
    <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
    </svg>
    <span class="hidden sm:inline">{{ label }}</span>
  </button>
</template>
